import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { tokenRequest, REAUTH_GUARD_KEY } from '../config/msalConfig';

/**
 * Centralised API-token acquisition for the SPA — the single code path used by both the
 * API client and the useAuth hook. Implements the 2026-06-10 SPA auth standard:
 *
 *  - rule 2: `await initialize()` before any token call (MSAL Browser v3 requirement).
 *  - rule 4: on a "needs interaction" failure, fall back to interactive auth rather than
 *            dead-ending — guarded by a once-per-session sessionStorage flag that clears on
 *            the next successful silent acquire, so a user who genuinely can't get a token
 *            doesn't loop on redirect.
 *
 * The bug this fixes: `acquireTokenSilent` renews tokens through a hidden iframe, and when
 * the SSO session has lapsed that iframe can stall and surface as a `timed_out` /
 * `monitor_window_timeout` BrowserAuthError instead of `login_required`. Those are NOT
 * `InteractionRequiredAuthError`, so the previous code logged "unhandled" and dead-ended.
 * Treating the timeout codes as needs-interaction routes them to the interactive fallback.
 */
const INTERACTIVE_ERROR_CODES = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'timed_out',
  'monitor_window_timeout',
]);

/** True when silent acquisition can't proceed and we must go interactive. */
export function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const code = (err as { errorCode?: string } | null)?.errorCode;
  return typeof code === 'string' && INTERACTIVE_ERROR_CODES.has(code);
}

/**
 * Acquire an API access token for the given account. Returns the raw token, or `null` when a
 * redirect is now in progress or acquisition genuinely failed (callers treat null as "no auth
 * header" / unauthenticated). Pass `forceRefresh` to bypass the token cache (e.g. just after a
 * role/permission change) per rule 3.
 */
export async function acquireApiToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
  opts?: { forceRefresh?: boolean },
): Promise<string | null> {
  // rule 2 — idempotent; cheap safety net against a request that races app bootstrap.
  await instance.initialize();
  try {
    const res = await instance.acquireTokenSilent({
      ...tokenRequest,
      account,
      forceRefresh: opts?.forceRefresh ?? false,
    });
    sessionStorage.removeItem(REAUTH_GUARD_KEY); // success → reset the loop guard
    return res.accessToken;
  } catch (err) {
    // rule 4 — interactive fallback (incl. the timed_out / monitor_window_timeout cases),
    // fired at most once per session and cleared on the next successful acquire above.
    if (needsInteraction(err) && !sessionStorage.getItem(REAUTH_GUARD_KEY)) {
      sessionStorage.setItem(REAUTH_GUARD_KEY, '1');
      try {
        await instance.acquireTokenRedirect({ ...tokenRequest, account });
      } catch {
        sessionStorage.removeItem(REAUTH_GUARD_KEY);
      }
      return null; // redirect in progress
    }
    console.warn('[auth] token acquisition failed:', err);
    return null;
  }
}
